import React, { useState, useEffect } from 'react';
import { Plus, Search, Edit, Trash2, Package, AlertTriangle, Eye, ArrowLeft, DollarSign, History, Building2 } from 'lucide-react';
import apiService from '../services/apiService';

const ExpensesTrack = () => {
  const [expenses, setExpenses] = useState([]);
  const [filteredExpenses, setFilteredExpenses] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentView, setCurrentView] = useState('list');
  const [error, setError] = useState('');
  const [selectedExpense, setSelectedExpense] = useState(null);

  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);

  // Company state
  const [companies, setCompanies] = useState([]);
  const [selectedCompanyId, setSelectedCompanyId] = useState('');

  // Product selection states
  const [productSearch, setProductSearch] = useState('');
  const [showProductDropdown, setShowProductDropdown] = useState(false);
  const [filteredProducts, setFilteredProducts] = useState([]);

  // Source of funds options
  const sourceOfFunds = ['SELF', 'BANK', 'OTHERS'];

  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentExpenses = filteredExpenses.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(filteredExpenses.length / itemsPerPage);

  // Form state
  const [formData, setFormData] = useState({
    productId: '',
    productName: '',
    name: '',
    description: '',
    vendor: '',
    sourceOfFunds: 'SELF',
    totalPrice: '',
    paidAmount: '',
    quantity: 1,
    expenseDate: new Date().toISOString().split('T')[0],
    companyId: ''
  });

  // Payment form state
  const [paymentForm, setPaymentForm] = useState({
    amount: '',
    sourceOfFunds: 'STL',
    paymentDate: new Date().toISOString().split('T')[0],
    notes: ''
  });

  // Load companies on mount
  useEffect(() => {
    loadCompanies();
  }, []);

  // Reload data when company changes
  useEffect(() => {
    if (selectedCompanyId) {
      loadExpenses();
      loadProducts();
    }
  }, [selectedCompanyId]);

  // Filter products for autocomplete
  useEffect(() => {
    if (productSearch) {
      const filtered = products.filter(product =>
        product.name.toLowerCase().includes(productSearch.toLowerCase()) ||
        product.sku?.toLowerCase().includes(productSearch.toLowerCase())
      );
      setFilteredProducts(filtered);
    } else {
      setFilteredProducts(products);
    }
  }, [productSearch, products]);

  // Click outside handler for dropdowns
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest('.product-search')) {
        setShowProductDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Filter expenses when search term changes
  useEffect(() => {
    filterExpenses();
  }, [expenses, searchTerm]);

  const loadCompanies = async () => {
    try {
      const data = await apiService.getAllCompanies();
      setCompanies(data);
      const savedCompanyId = localStorage.getItem('selectedCompanyId');
      if (savedCompanyId && data.find(c => c.id === parseInt(savedCompanyId))) {
        setSelectedCompanyId(savedCompanyId);
      } else if (data.length > 0) {
        setSelectedCompanyId(data[0].id.toString());
        localStorage.setItem('selectedCompanyId', data[0].id.toString());
      } else {
        setLoading(false);
      }
    } catch (error) {
      console.error('Error loading companies:', error);
      setLoading(false);
    }
  };

  const handleCompanyChange = (e) => {
    const companyId = e.target.value;
    setSelectedCompanyId(companyId);
    localStorage.setItem('selectedCompanyId', companyId);
    setCurrentPage(1);
  };

  const loadExpenses = async () => {
    try {
      setLoading(true);
      apiService.refreshTokenFromStorage();
      const data = selectedCompanyId
        ? await apiService.getExpensesByCompany(selectedCompanyId)
        : await apiService.getAllExpenses();
      setExpenses(data);
    } catch (error) {
      setError('Failed to load expenses');
      console.error('Error loading expenses:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadProducts = async () => {
    try {
      const data = selectedCompanyId
        ? await apiService.getActiveProductsByCompany(selectedCompanyId)
        : await apiService.getActiveProducts();
      setProducts(data);
    } catch (error) {
      console.error('Error loading products:', error);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-GB');
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-KE', {
      style: 'currency',
      currency: 'KES'
    }).format(amount || 0);
  };

  const filterExpenses = () => {
    let filtered = [...expenses];

    if (searchTerm) {
      filtered = filtered.filter(expense =>
        expense.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        expense.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        expense.vendor?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    setFilteredExpenses(filtered);
  };

  const handleSelectProduct = (product) => {
    setFormData({
      ...formData,
      productId: product.id,
      productName: product.name,
      name: product.name,
      totalPrice: product.price.toString(),
      vendor: '',
      description: product.description || ''
    });
    setProductSearch(product.name);
    setShowProductDropdown(false);
  };

  const calculateBalance = () => {
    const total = parseFloat(formData.totalPrice) || 0;
    const paid = parseFloat(formData.paidAmount) || 0;
    return total - paid;
  };

  const handleCreate = () => {
    setCurrentView('create');
    setSelectedExpense(null);
    setFormData({
      productId: '',
      productName: '',
      name: '',
      description: '',
      vendor: '',
      sourceOfFunds: 'SELF',
      totalPrice: '',
      paidAmount: '',
      quantity: 1,
      expenseDate: new Date().toISOString().split('T')[0],
      companyId: selectedCompanyId
    });
    setProductSearch('');
    setError('');
  };

  const handleEdit = (expense) => {
    setCurrentView('edit');
    setSelectedExpense(expense);
    setFormData({
      productId: expense.productId || '',
      productName: expense.productName || '',
      name: expense.name,
      description: expense.description || '',
      vendor: expense.vendor || '',
      sourceOfFunds: expense.sourceOfFunds || 'STL',
      totalPrice: expense.totalPrice?.toString() || '',
      paidAmount: expense.paidAmount?.toString() || '',
      quantity: expense.quantity || 1,
      expenseDate: expense.expenseDate || '',
      companyId: expense.companyId || selectedCompanyId
    });
    setProductSearch(expense.productName || expense.name);
    setError('');
  };

  const handleView = (expense) => {
    setCurrentView('view');
    setSelectedExpense(expense);
  };

  const handleAddPayment = (expense) => {
    setSelectedExpense(expense);
    setCurrentView('payment');
    const balance = expense.balanceAmount || 0;
    setPaymentForm({
      amount: balance.toString(),
      sourceOfFunds: 'STL',
      paymentDate: new Date().toISOString().split('T')[0],
      notes: ''
    });
  };

  const handleBackToList = () => {
    setCurrentView('list');
    setSelectedExpense(null);
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSubmitting(true);

    try {
      const expenseData = {
        name: formData.name,
        description: formData.description,
        vendor: formData.vendor,
        sourceOfFunds: formData.sourceOfFunds,
        price: parseFloat(formData.totalPrice),
        paidAmount: parseFloat(formData.paidAmount) || 0,
        quantityInStock: parseInt(formData.quantity) || 1,
        expenseDate: formData.expenseDate,
        paymentmethod: formData.sourceOfFunds,
        productId: formData.productId ? parseInt(formData.productId) : null,
        companyId: parseInt(formData.companyId || selectedCompanyId)
      };

      if (currentView === 'create') {
        await apiService.createExpense(expenseData);
      } else {
        await apiService.updateExpense(selectedExpense.id, expenseData);
      }

      loadExpenses();
      handleBackToList();
    } catch (error) {
      setError(error.message || 'Failed to save expense');
    } finally {
      setSubmitting(false);
    }
  };

  const handleSubmitPayment = async (e) => {
    e.preventDefault();
    setError('');
    setSubmitting(true);

    try {
      const paymentData = {
        expenseId: selectedExpense.id,
        amount: parseFloat(paymentForm.amount),
        sourceOfFunds: paymentForm.sourceOfFunds,
        paymentDate: paymentForm.paymentDate,
        notes: paymentForm.notes
      };

      await apiService.createExpensePayment(paymentData);
      loadExpenses();
      handleBackToList();
    } catch (error) {
      setError(error.message || 'Failed to record payment');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (expenseId) => {
    if (window.confirm('Are you sure you want to delete this expense?')) {
      try {
        await apiService.deleteExpense(expenseId);
        loadExpenses();
      } catch (error) {
        setError('Failed to delete expense');
      }
    }
  };

  const getCompanyName = (companyId) => {
    const company = companies.find(c => c.id === parseInt(companyId));
    return company ? company.name : '';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  // Expense List View
  if (currentView === 'list') {
    return (
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Expense Management</h1>
            <p className="text-gray-600">Manage your business expenses and track payments</p>
          </div>
          <button
            onClick={handleCreate}
            className="mt-4 sm:mt-0 bg-indigo-600 text-white px-4 py-2 rounded-lg hover:bg-indigo-700 transition-colors flex items-center space-x-2"
          >
            <Plus className="w-4 h-4" />
            <span>Add Expense</span>
          </button>
        </div>

        {/* Error Message */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <div className="flex items-center">
              <AlertTriangle className="h-5 w-5 text-red-400 mr-2" />
              <span className="text-sm text-red-700">{error}</span>
            </div>
          </div>
        )}

        {/* Filters */}
        <div className="bg-white rounded-lg shadow-sm border p-6">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            {/* Company Filter */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Company</label>
              <div className="flex items-center space-x-2">
                <Building2 className="w-4 h-4 text-gray-400" />
                <select
                  value={selectedCompanyId}
                  onChange={handleCompanyChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                >
                  {companies.map(company => (
                    <option key={company.id} value={company.id}>{company.name}</option>
                  ))}
                </select>
              </div>
            </div>

            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-2">Search</label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                <input
                  type="text"
                  placeholder="Search expenses..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10 pr-4 py-2 w-full border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                />
              </div>
            </div>

            <div className="flex items-end">
              <button
                onClick={() => setSearchTerm('')}
                className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors w-full"
              >
                Clear Filters
              </button>
            </div>
          </div>
        </div>

        {/* Expense Table */}
        <div className="bg-white rounded-lg shadow-sm border overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Vendor</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Total</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Paid</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Balance</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Source</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {currentExpenses.map((expense) => (
                  <tr key={expense.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="flex-shrink-0 h-10 w-10">
                          <div className="h-10 w-10 rounded-lg bg-orange-100 flex items-center justify-center">
                            <Package className="h-5 w-5 text-orange-600" />
                          </div>
                        </div>
                        <div className="ml-4">
                          <div className="text-sm font-medium text-gray-900">{expense.name}</div>
                          <div className="text-sm text-gray-500 truncate max-w-xs">{expense.description}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{expense.vendor || '-'}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{formatDate(expense.expenseDate)}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{formatCurrency(expense.totalPrice)}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-green-600">{formatCurrency(expense.paidAmount)}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      <span className={`font-medium ${expense.balanceAmount > 0 ? 'text-red-600' : 'text-green-600'}`}>
                        {formatCurrency(expense.balanceAmount)}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                        {expense.sourceOfFunds || '-'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <div className="flex items-center space-x-2">
                        <button
                          onClick={() => handleView(expense)}
                          className="text-indigo-600 hover:text-indigo-900"
                          title="View"
                        >
                          <Eye className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleEdit(expense)}
                          className="text-blue-600 hover:text-blue-900"
                          title="Edit"
                        >
                          <Edit className="w-4 h-4" />
                        </button>
                        {expense.balanceAmount > 0 && (
                          <button
                            onClick={() => handleAddPayment(expense)}
                            className="text-green-600 hover:text-green-900"
                            title="Add Payment"
                          >
                            <DollarSign className="w-4 h-4" />
                          </button>
                        )}
                        <button
                          onClick={() => handleDelete(expense.id)}
                          className="text-red-600 hover:text-red-900"
                          title="Delete"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
                <div className="flex-1 flex justify-between sm:hidden">
                  <button
                    onClick={() => setCurrentPage(Math.max(currentPage - 1, 1))}
                    disabled={currentPage === 1}
                    className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
                  >
                    Previous
                  </button>
                  <button
                    onClick={() => setCurrentPage(Math.min(currentPage + 1, totalPages))}
                    disabled={currentPage === totalPages}
                    className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
                  >
                    Next
                  </button>
                </div>
                <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                  <div>
                    <p className="text-sm text-gray-700">
                      Showing <span className="font-medium">{indexOfFirstItem + 1}</span> to{' '}
                      <span className="font-medium">{Math.min(indexOfLastItem, filteredExpenses.length)}</span> of{' '}
                      <span className="font-medium">{filteredExpenses.length}</span> results
                    </p>
                  </div>
                  <div>
                    <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px">
                      <button
                        onClick={() => setCurrentPage(Math.max(currentPage - 1, 1))}
                        disabled={currentPage === 1}
                        className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50"
                      >
                        Previous
                      </button>
                      {[...Array(totalPages)].map((_, index) => {
                        const pageNumber = index + 1;
                        if (pageNumber === 1 || pageNumber === totalPages || (pageNumber >= currentPage - 1 && pageNumber <= currentPage + 1)) {
                          return (
                            <button key={pageNumber} onClick={() => setCurrentPage(pageNumber)}
                              className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                                pageNumber === currentPage
                                  ? 'z-10 bg-indigo-50 border-indigo-500 text-indigo-600'
                                  : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                              }`}>{pageNumber}</button>
                          );
                        } else if (pageNumber === currentPage - 2 || pageNumber === currentPage + 2) {
                          return <span key={pageNumber} className="relative inline-flex items-center px-4 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-700">...</span>;
                        }
                        return null;
                      })}
                      <button
                        onClick={() => setCurrentPage(Math.min(currentPage + 1, totalPages))}
                        disabled={currentPage === totalPages}
                        className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50"
                      >
                        Next
                      </button>
                    </nav>
                  </div>
                </div>
              </div>
            )}
          </div>

          {currentExpenses.length === 0 && (
            <div className="text-center py-12">
              <Package className="mx-auto h-12 w-12 text-gray-400" />
              <h3 className="mt-2 text-sm font-medium text-gray-900">No expenses found</h3>
              <p className="mt-1 text-sm text-gray-500">Get started by adding a new expense.</p>
            </div>
          )}
        </div>
      </div>
    );
  }

  // Create/Edit Expense Form
  if (currentView === 'create' || currentView === 'edit') {
    return (
      <div className="space-y-6">
        <div className="flex items-center space-x-4">
          <button onClick={handleBackToList} className="p-2 rounded-lg border border-gray-300 hover:bg-gray-50 transition-colors">
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">
              {currentView === 'create' ? 'Add New Expense' : 'Edit Expense'}
            </h1>
            <p className="text-gray-600">
              {currentView === 'create' ? 'Fill in the details to record a new expense' : 'Update expense information'}
            </p>
          </div>
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <div className="flex items-center">
              <AlertTriangle className="h-5 w-5 text-red-400 mr-2" />
              <span className="text-sm text-red-700">{error}</span>
            </div>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="bg-white rounded-lg shadow-sm border p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Expense Details</h3>
            <div className="space-y-4">
              {/* Company Selection */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Company *</label>
                <select
                  required
                  value={formData.companyId}
                  onChange={(e) => setFormData({...formData, companyId: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                >
                  <option value="">Select Company</option>
                  {companies.map(company => (
                    <option key={company.id} value={company.id}>{company.name}</option>
                  ))}
                </select>
              </div>

              {/* Link to Product (optional) */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Link to Product (Optional)</label>
                <div className="relative product-search">
                  <input
                    type="text"
                    placeholder="Search products to link..."
                    value={productSearch}
                    onChange={(e) => { setProductSearch(e.target.value); setShowProductDropdown(true); }}
                    onFocus={() => setShowProductDropdown(true)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  />
                  {showProductDropdown && filteredProducts.length > 0 && (
                    <div className="absolute z-10 w-full bg-white border border-gray-300 rounded-lg mt-1 max-h-60 overflow-y-auto shadow-lg">
                      {filteredProducts.slice(0, 10).map(product => (
                        <div
                          key={product.id}
                          onClick={() => handleSelectProduct(product)}
                          className="px-3 py-2 cursor-pointer hover:bg-gray-100 border-b last:border-b-0"
                        >
                          <div className="font-medium text-sm">{product.name}</div>
                          <div className="text-xs text-gray-500">{product.sku} - {formatCurrency(product.price)}</div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Expense Name *</label>
                  <input
                    type="text"
                    required
                    value={formData.name}
                    onChange={(e) => setFormData({...formData, name: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Vendor</label>
                  <input
                    type="text"
                    value={formData.vendor}
                    onChange={(e) => setFormData({...formData, vendor: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                <textarea
                  rows={3}
                  value={formData.description}
                  onChange={(e) => setFormData({...formData, description: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Total Price *</label>
                  <input
                    type="number"
                    step="0.01"
                    required
                    value={formData.totalPrice}
                    onChange={(e) => setFormData({...formData, totalPrice: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Paid Amount</label>
                  <input
                    type="number"
                    step="0.01"
                    value={formData.paidAmount}
                    onChange={(e) => setFormData({...formData, paidAmount: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Quantity</label>
                  <input
                    type="number"
                    min="1"
                    value={formData.quantity}
                    onChange={(e) => setFormData({...formData, quantity: parseInt(e.target.value) || 1})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Balance</label>
                  <input
                    type="text"
                    readOnly
                    value={formatCurrency(calculateBalance())}
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg bg-gray-50 text-gray-700"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Source of Funds</label>
                  <select
                    value={formData.sourceOfFunds}
                    onChange={(e) => setFormData({...formData, sourceOfFunds: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  >
                    {sourceOfFunds.map(source => (
                      <option key={source} value={source}>{source}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Expense Date *</label>
                  <input
                    type="date"
                    required
                    value={formData.expenseDate}
                    onChange={(e) => setFormData({...formData, expenseDate: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Form Actions */}
          <div className="flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-4">
            <button
              type="submit"
              disabled={submitting}
              className="flex-1 sm:flex-none px-6 py-3 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed"
            >
              {submitting ? 'Saving...' : (currentView === 'create' ? 'Add Expense' : 'Update Expense')}
            </button>
            <button
              type="button"
              onClick={handleBackToList}
              disabled={submitting}
              className="flex-1 sm:flex-none px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
            >
              Cancel
            </button>
          </div>
        </form>
      </div>
    );
  }

  // View Expense Details
  if (currentView === 'view') {
    return (
      <div className="space-y-6">
        <div className="flex items-center space-x-4">
          <button onClick={handleBackToList} className="p-2 rounded-lg border border-gray-300 hover:bg-gray-50 transition-colors">
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Expense Details</h1>
            <p className="text-gray-600">{selectedExpense?.name}</p>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm border p-6">
          <div className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-gray-700">Company</label>
              <p className="mt-1 text-sm text-gray-900">{getCompanyName(selectedExpense?.companyId) || 'N/A'}</p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700">Name</label>
                <p className="mt-1 text-sm text-gray-900">{selectedExpense?.name}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Vendor</label>
                <p className="mt-1 text-sm text-gray-900">{selectedExpense?.vendor || 'N/A'}</p>
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Description</label>
              <p className="mt-1 text-sm text-gray-900">{selectedExpense?.description || 'No description'}</p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700">Total Price</label>
                <p className="mt-1 text-sm font-medium text-gray-900">{formatCurrency(selectedExpense?.totalPrice)}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Paid Amount</label>
                <p className="mt-1 text-sm font-medium text-green-600">{formatCurrency(selectedExpense?.paidAmount)}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Balance</label>
                <p className="mt-1 text-sm font-medium text-red-600">{formatCurrency(selectedExpense?.balanceAmount)}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Quantity</label>
                <p className="mt-1 text-sm text-gray-900">{selectedExpense?.quantity || 1}</p>
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700">Source of Funds</label>
                <p className="mt-1">
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                    {selectedExpense?.sourceOfFunds || 'N/A'}
                  </span>
                </p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Date</label>
                <p className="mt-1 text-sm text-gray-900">{formatDate(selectedExpense?.expenseDate)}</p>
              </div>
            </div>

            {/* Payment History */}
            {selectedExpense?.payments && selectedExpense.payments.length > 0 && (
              <div>
                <h4 className="text-lg font-medium text-gray-900 mb-3">Payment History</h4>
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Amount</th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Source</th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Notes</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {selectedExpense.payments.map((payment, index) => (
                        <tr key={index}>
                          <td className="px-4 py-2 text-sm text-gray-900">{formatDate(payment.paymentDate)}</td>
                          <td className="px-4 py-2 text-sm text-green-600 font-medium">{formatCurrency(payment.amount)}</td>
                          <td className="px-4 py-2 text-sm text-gray-900">{payment.sourceOfFunds || '-'}</td>
                          <td className="px-4 py-2 text-sm text-gray-500">{payment.notes || '-'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  // Payment Form
  if (currentView === 'payment') {
    return (
      <div className="space-y-6">
        <div className="flex items-center space-x-4">
          <button onClick={handleBackToList} className="p-2 rounded-lg border border-gray-300 hover:bg-gray-50 transition-colors">
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Add Payment</h1>
            <p className="text-gray-600">
              {selectedExpense?.name} - Balance: {formatCurrency(selectedExpense?.balanceAmount)}
            </p>
          </div>
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <div className="flex items-center">
              <AlertTriangle className="h-5 w-5 text-red-400 mr-2" />
              <span className="text-sm text-red-700">{error}</span>
            </div>
          </div>
        )}

        <div className="bg-white rounded-lg shadow-sm border p-6">
          <form onSubmit={handleSubmitPayment} className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Payment Amount *</label>
                <input
                  type="number"
                  step="0.01"
                  required
                  max={selectedExpense?.balanceAmount}
                  value={paymentForm.amount}
                  onChange={(e) => setPaymentForm({...paymentForm, amount: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Payment Date *</label>
                <input
                  type="date"
                  required
                  value={paymentForm.paymentDate}
                  onChange={(e) => setPaymentForm({...paymentForm, paymentDate: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Source of Funds *</label>
              <select
                required
                value={paymentForm.sourceOfFunds}
                onChange={(e) => setPaymentForm({...paymentForm, sourceOfFunds: e.target.value})}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
              >
                {sourceOfFunds.map(source => (
                  <option key={source} value={source}>{source}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
              <textarea
                rows={3}
                value={paymentForm.notes}
                onChange={(e) => setPaymentForm({...paymentForm, notes: e.target.value})}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                placeholder="Payment notes..."
              />
            </div>

            <div className="flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-4">
              <button
                type="submit"
                disabled={submitting}
                className="flex-1 sm:flex-none px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed"
              >
                {submitting ? 'Recording...' : 'Record Payment'}
              </button>
              <button
                type="button"
                onClick={handleBackToList}
                disabled={submitting}
                className="flex-1 sm:flex-none px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      </div>
    );
  }

  return null;
};

export default ExpensesTrack;